// Events Manager Component
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { EventForm } from './AdminShared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEvents(data || []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/events/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const handleSave = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/events/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setEditingId(null);
      fetchEvents();
    } catch (err) {
      console.error('Error saving event:', err);
    }
  };

  const handleAdd = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/events`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setShowAddForm(false);
      setEditForm({});
      fetchEvents();
    } catch (err) {
      console.error('Error adding event:', err);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-slate-800">Événements</h2>
        <Button onClick={() => {
          setShowAddForm(true);
          setEditForm({ title_en: '', title_fr: '', title_ar: '', description_en: '', description_fr: '', description_ar: '', date: '', location: '', maps_url: '', image_url: '', is_active: true });
        }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {showAddForm && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-800 mb-4">Nouvel Événement</h3>
          <EventForm form={editForm} setForm={setEditForm} onSave={handleAdd} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      <div className="space-y-4">
        {events.map((event) => (
          <div key={event.id} className="border border-slate-200 rounded-lg p-4">
            {editingId === event.id ? (
              <EventForm form={editForm} setForm={setEditForm} onSave={() => handleSave(event.id)} onCancel={() => setEditingId(null)} />
            ) : (
              <div className="flex items-center gap-4">
                {event.image_url && <img src={event.image_url} alt="" className="w-24 h-16 object-cover rounded" />}
                <div className="flex-1">
                  <h3 className="font-medium text-slate-800">{event.title_fr || event.title_en}</h3>
                  <p className="text-sm text-slate-500">{event.date} - {event.location}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingId(event.id); setEditForm(event); }}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {events.length === 0 && !showAddForm && <div className="text-center py-12 text-slate-500">Aucun événement.</div>}
    </div>
  );
};

export default EventsManager;
